import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useSignInMutation } from '../../api/index.js';
import { Button } from '../../design-system/button.js';
import { Field, FieldError, FieldLabel, Input } from '../../design-system/form.js';
import { SessionLoadingBoundary, SessionUnavailableBoundary } from '../../app/app-boundaries.js';
import type { SessionResource } from '../../app/runtime.js';
import { signInErrorMessage } from './login-errors.js';

const schema = z.object({
  email: z.string().trim().email('Enter a valid email address.'),
  password: z.string().min(1, 'Enter your password.'),
});
type LoginFields = z.infer<typeof schema>;

export function LoginPage({ session }: { session: SessionResource }) {
  const navigate = useNavigate();
  const location = useLocation();
  const signIn = useSignInMutation();
  const form = useForm<LoginFields>({
    defaultValues: { email: 'shaun@aol.com', password: '' },
    resolver: zodResolver(schema),
  });

  if (session.status === 'authenticated') return <Navigate to="/create" replace />;
  if (session.status === 'loading') return <SessionLoadingBoundary />;
  if (session.status === 'unavailable') return <SessionUnavailableBoundary resource={session} />;

  const destination =
    typeof location.state === 'object' && location.state && 'from' in location.state
      ? String(location.state.from)
      : '/create';

  return (
    <main className="grid min-h-svh place-items-center bg-bg px-5 py-12 text-ink">
      <section className="w-full max-w-md">
        <div className="flex items-center gap-3">
          <span className="grid size-11 place-items-center rounded-lg bg-accent font-mono text-base font-semibold text-accent-ink">
            5
          </span>
          <span className="font-semibold tracking-tight">Song Composer</span>
        </div>
        <form
          className="mt-10 grid gap-6 rounded-xl border border-border bg-surface p-6 shadow-sm sm:p-8"
          onSubmit={form.handleSubmit(async (values) => {
            try {
              await signIn.mutateAsync(values);
              navigate(destination, { replace: true });
            } catch {
              // Mutation state renders the classified error without replacing the current page.
            }
          })}
        >
          <h1 className="text-3xl font-semibold tracking-[-0.035em]">Sign in</h1>
          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              autoComplete="username"
              {...form.register('email')}
              aria-invalid={Boolean(form.formState.errors.email)}
            />
            {form.formState.errors.email ? (
              <FieldError>{form.formState.errors.email.message}</FieldError>
            ) : null}
          </Field>
          <Field>
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              {...form.register('password')}
              aria-invalid={Boolean(form.formState.errors.password)}
            />
            {form.formState.errors.password ? (
              <FieldError>{form.formState.errors.password.message}</FieldError>
            ) : null}
          </Field>
          {signIn.error ? <FieldError>{signInErrorMessage(signIn.error)}</FieldError> : null}
          <Button type="submit" disabled={signIn.isPending} className="w-full">
            {signIn.isPending ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </section>
    </main>
  );
}
